import React, { useState, useRef, useLayoutEffect } from "react";
import FilterBar from "../UI/FilterBar";
import SearchInputTable from "../common/SearchInputTable";
import styles from "../../_css/CustomTable.module.css";
import {
  MoreVertical,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Eye,
  PencilLineIcon,
  AudioLines,
  MessageSquareText,
} from "lucide-react";
import UserProfileModal from "../UI/UserProfileModal";
import DeleteUserModal from "../UI/DeleteUserModal";
import { createPortal } from "react-dom";
import SelectComponent from "../common/SelectComponent";
import TrainAIPopup from "../TrainModule/TrainAIPopup";
import SessionSummaryPopup from "../TrainModule/SessionSummaryPopup";
import ChatPopup from "../UI/ChatPopup";
import { ToggleSwitch } from "../UI/CustomButtons";
import {
  fetchPreviousTrainingSession,
  updateSessionVisibility,
} from "../../requests";
import { deleteAuthToken, notifyError } from "../../utils/helper";
import { formatDate } from "../helper/formatters";
import _ from "lodash";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer";
import { useDispatch } from "react-redux";
import ReviewSession from "../Modals/ReviewSession";
import { setReviewSessionTitle } from "../../_store/reducers/reviewReducer";

const PAGE_SIZE = 5;
const DROPDOWN_WIDTH = 180; // px

// Custom styles copied from SelectComponent
const dropdownMenuStyles = {
  position: "absolute",
  top: "36px",
  right: "0",
  minWidth: "180px",
  width: "180px",
  background: "#fff",
  borderRadius: "12px",
  boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
  zIndex: 10,
  padding: "6px 0",
  border: "1px solid #eaf6ff",
};
const dropdownOptionStyles = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  padding: "10px 18px",
  fontSize: "13px",
  color: "#6B7280",
  //   background: "#F2F9FD",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  margin: "4px 8px",
  transition: "background 0.2s",
};
const dropdownOptionHover = {
  background: "#F3F4F6",
};

function RowActionsDropdown({ onViewProfile, onRemoveUser, onReviewSession }) {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(null);
  const btnRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({
    top: 0,
    left: 0,
    width: 180,
  });

  useLayoutEffect(() => {
    if (open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY,
        left: rect.right + window.scrollX - DROPDOWN_WIDTH,
      });
    }
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (btnRef.current && btnRef.current.contains(e.target)) {
        return;
      }
      if (dropdownRef.current && dropdownRef.current.contains(e.target)) {
        return;
      }
      setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button
        className={styles.rowActionsBtn}
        onClick={() => setOpen((o) => !o)}
        tabIndex={0}
        aria-label="Row actions"
        style={{
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
        }}
        ref={btnRef}
      >
        <MoreVertical size={22} color="#798797" />
      </button>
      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              ...dropdownMenuStyles,
              position: "absolute",
              top: dropdownPos.top,
              left: dropdownPos.left,
              minWidth: "180px",
              width: "180px",
              zIndex: 9999,
            }}
          >
            <div
              style={
                hovered === 0
                  ? { ...dropdownOptionStyles, ...dropdownOptionHover }
                  : dropdownOptionStyles
              }
              onMouseEnter={() => setHovered(0)}
              onMouseLeave={() => setHovered(null)}
              tabIndex={0}
              onClick={() => {
                setOpen(false);
                onViewProfile && onViewProfile();
              }}
            >
              <PencilLineIcon size={18} /> Edit Session
            </div>
            <div
              style={
                hovered === 1
                  ? { ...dropdownOptionStyles, ...dropdownOptionHover }
                  : dropdownOptionStyles
              }
              onMouseEnter={() => setHovered(1)}
              onMouseLeave={() => setHovered(null)}
              tabIndex={0}
              onClick={() => {
                setOpen(false);
                onRemoveUser && onRemoveUser();
              }}
            >
              <Trash2 size={18} /> Remove Session
            </div>
            <div
              style={
                hovered === 2
                  ? { ...dropdownOptionStyles, ...dropdownOptionHover }
                  : dropdownOptionStyles
              }
              onMouseEnter={() => setHovered(2)}
              onMouseLeave={() => setHovered(null)}
              tabIndex={0}
              onClick={() => {
                setOpen(false);
                onRemoveUser && onReviewSession();
              }}
            >
              <Eye size={18} /> Review Session
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

const CustomTable = ({
  headers,
  rows,
  onFilterChange,
  searchConfig,
  variant = "blue",
  pagination,
  onPageChange,
  onVisibilityFilter,
  onSearchChange,
  searchValue,
  onDateFilterChange,
  trainingHistory = false,
  onModeChange,
  onSummaryChange,
}) => {
  const [selectedRows, setSelectedRows] = useState([]); // store indices of selected rows
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [deleteTargetIdx, setDeleteTargetIdx] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [showSummaryPopup, setShowSummaryPopup] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [showChatReview, setShowChatReview] = useState(false);
  const [chatData, setChatData] = useState({
    chatMsgs: [],
    isSummary: false,
  });
  const [data, setData] = useState(null);
  const [reviewSessionData, setReviewSessionData] = useState(null);

  const dispatch = useDispatch();
  // Pagination logic - use API pagination data
  const totalPages = pagination?.totalPages || 1;
  const totalEntries = pagination?.totalItems || rows.length;
  const currentPage = pagination?.currentPage || 1;
  const itemsPerPage = pagination?.itemsPerPage || PAGE_SIZE;

  // Use rows directly since they're already paginated from API
  const pagedRows = rows;

  // Map pagedRows to their global indices - now using row IDs
  const pagedRowIndices = pagedRows.map((row, idx) => row.id);

  const allVisibleSelected = pagedRowIndices.every((id) =>
    selectedRows.includes(id)
  );
  const someVisibleSelected = pagedRowIndices.some((id) =>
    selectedRows.includes(id)
  );

  const handleEditSession = (id) => {
    const row = rows.find((row) => row.id === id);
    setData(row);
    setShowPopup(true);
  };

  const handleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedRows(
        selectedRows.filter((idx) => !pagedRowIndices.includes(idx))
      );
    } else {
      setSelectedRows([
        ...selectedRows.filter((idx) => !pagedRowIndices.includes(idx)),
        ...pagedRowIndices,
      ]);
    }
  };

  const handleSelectRow = (rowId) => {
    if (selectedRows.includes(rowId)) {
      setSelectedRows(selectedRows.filter((id) => id !== rowId));
    } else {
      setSelectedRows([...selectedRows, rowId]);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && onPageChange) {
      onPageChange(newPage, itemsPerPage);
    }
  };

  const handleToggleVisibility = async (value, id) => {
    dispatch(startLoader());
    try {
      await updateSessionVisibility({
        id,
        status: value,
      });
      // Trigger a refresh of the current page to get updated data
      const currentPageInfo = pagination?.currentPage || 1;
      const currentLimit = pagination?.itemsPerPage || 5;
      onPageChange(currentPageInfo, currentLimit);
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken();
        window.location.reload();
      }
      console.error("Error updating session visibility:", error);
    }
    dispatch(stopLoader());
  };

  const handleVisibilityChange = (value) => {
    onVisibilityFilter(value?.value);
  };

  const handleUpdateSession = async () => {
    const sessionId = data.id;
    if (!sessionId) {
      notifyError("Session ID is required");
      return;
    }
    setShowPopup(false);
    dispatch(startLoader());
    try {
      const response = await fetchPreviousTrainingSession({ sessionId });
      setChatData(response.data);
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken();
        window.location.reload();
      }
      console.error("Error updating session visibility:", error);
    }
    dispatch(stopLoader());
    sessionStorage.setItem("trainerSessionId", sessionId);
    setShowChat(true);
  };

  const handleReviewSession = (row) => {
    setReviewSessionData(row);
    setShowChatReview(true);
  };

  return (
    <div
      className={`${
        trainingHistory
          ? styles.tableContainerTrainingHistory
          : styles.tableContainer
      } shadow-none bg-white`}
    >
      <ChatPopup
        show={showChat}
        onClose={() => setShowChat(false)}
        chatData={chatData}
        from="summaryPage"
      />
      {/* <ChatPopup
        show={showChatReview}
        onClose={() => setShowChatReview(false)}
        from="reviewSession"
      /> */}
      <ReviewSession
        show={showChatReview}
        onClose={() => setShowChatReview(false)}
        onEndSession={() => setShowChatReview(false)}
        sessionId={reviewSessionData?.id}
      />
      <TrainAIPopup
        show={showPopup}
        onHide={() => setShowPopup(false)}
        data={data}
        editMode={true}
        modelTitle="Edit Session"
        onOpenSummary={(summary) => {
          setSummaryData({ ...data, summary });
          setShowSummaryPopup(true);
          setShowPopup(false);
        }}
        // onOpenChat={handleOpenChat}
      />
      <SessionSummaryPopup
        show={showSummaryPopup}
        onHide={() => setShowSummaryPopup(false)}
        data={summaryData}
        openChat={handleUpdateSession}
      />
      {/* Bulk Actions Bar */}
      {/* {selectedRows.length > 0 && (
        <div className="d-flex align-items-center gap-3 p-3 bg-light border-bottom">
          <span className="text-muted">
            {selectedRows.length} session(s) selected
          </span>
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => {
              // Implement bulk visibility toggle
              console.log("Bulk visibility toggle for:", selectedRows);
            }}
          >
            Toggle Visibility
          </button>
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={() => {
              setDeleteTargetIdx(selectedRows[0]); // Use first selected for modal
              setDeleteModalOpen(true);
            }}
          >
            Delete Selected
          </button>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setSelectedRows([])}
          >
            Clear Selection
          </button>
        </div>
      )} */}

      <div className={styles.filtersRow}>
        <div className="d-flex align-items-center gap-3">
          {!trainingHistory && (
            <SelectComponent
              options={[
                { label: "Visible to Users", value: 1 },
                { label: "Hidden from Users", value: 0 },
              ]}
              placeholder="Visibility"
              handleValueChange={handleVisibilityChange}
              disabled={false}
              isMulti={false}
              isClearable={false}
              value={null}
            />
          )}
          {trainingHistory && (
            <>
              <SelectComponent
                options={[
                  { label: "Voice", value: "voice" },
                  { label: "Text", value: "text" },
                ]}
                placeholder="Mode"
                handleValueChange={onModeChange}
                disabled={false}
                isMulti={false}
                isClearable={false}
                value={null}
              />
              <SelectComponent
                options={[
                  { label: "Edited", value: 1 },
                  { label: "Not Edited", value: 0 },
                ]}
                placeholder="Summary"
                handleValueChange={onSummaryChange}
                disabled={false}
                isMulti={false}
                isClearable={false}
                value={null}
              />
            </>
          )}
          <FilterBar
            onFilterChange={onDateFilterChange}
            showExtraFilter={false}
            showEndDate={false}
          />
        </div>
        <SearchInputTable
          value={searchValue}
          onChange={onSearchChange}
          placeholder={searchConfig.placeholder}
          column={searchConfig.column}
          width="120px"
          padding="0px 8px"
          paddingContainer="0 8px 0 0"
        />
      </div>

      <div className={`${styles.tableWrapper} mt-2`}>
        <div className={styles.tableBorderWrapper}>
          <table className={styles.usersTable}>
            <thead>
              <tr
                style={{
                  background: variant === "blue" ? "#2595D2" : "#F0FDF4",
                }}
              >
                <th
                  style={{
                    width: "4rem",
                    color: variant === "blue" ? "#fff" : "#14532B",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    ref={(el) => {
                      if (el)
                        el.indeterminate =
                          !allVisibleSelected && someVisibleSelected;
                    }}
                    onChange={handleSelectAll}
                    className={styles.checkbox}
                  />
                  <label style={{ marginLeft: "8px" }}>S.L</label>
                </th>

                {headers.map((header, idx) => (
                  <th
                    // style={{
                    //   width:
                    //     header === "Session Title"
                    //       ? "35%"
                    //       : header === "Created Date"
                    //       ? "20%"
                    //       : header === "Status"
                    //       ? "20%"
                    //       : header === "Visiblity"
                    //       ? "17%"
                    //       : "auto",
                    //   color: variant === "blue" ? "#fff" : "#14532B",
                    // }}
                    style={{
                      color: variant === "blue" ? "#fff" : "#14532B",
                    }}
                    key={idx}
                  >
                    {header}
                  </th>
                ))}

                <th
                  style={{
                    width: "4rem",
                    color: variant === "blue" ? "#fff" : "#14532B",
                  }}
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.map((row, idx) => {
                const rowNumber = (currentPage - 1) * itemsPerPage + idx + 1;
                return (
                  <tr key={row.id}>
                    <td className={styles.tableCell}>
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(row.id)}
                        onChange={() => handleSelectRow(row.id)}
                        className={styles.checkbox}
                      />
                      <span style={{ marginLeft: "8px" }}>{rowNumber}</span>
                    </td>

                    <td className={styles.tableCell}>{row.title}</td>
                    {trainingHistory ? (
                      <td className={styles.tableCell}>
                        <span
                          className={
                            _.capitalize(row.mode) === "Voice"
                              ? styles.voiceBadge
                              : styles.textBadge
                          }
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            width: "fit-content",
                          }}
                        >
                          {_.capitalize(row.mode) === "Voice" ? (
                            <AudioLines size={16} />
                          ) : (
                            <MessageSquareText size={16} />
                          )}
                          {_.capitalize(row.mode) || "Text"}
                        </span>
                      </td>
                    ) : (
                      <td className={styles.tableCell}>
                        {formatDate(row.created_at)}
                      </td>
                    )}
                    {trainingHistory ? (
                      <td className={styles.tableCell}>
                        {formatDate(row.created_at)}
                      </td>
                    ) : (
                      <td className={styles.tableCell}>
                        <span
                          className={
                            row.status === 1
                              ? styles.publishedBadge
                              : styles.draftBadge
                          }
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            width: "fit-content",
                          }}
                        >
                          {row.status === 1 ? "Published" : "Draft"}
                        </span>
                      </td>
                    )}

                    {trainingHistory ? (
                      <td className={styles.tableCell}>
                        {row.summary_edited ? "Yes" : "No"}
                      </td>
                    ) : (
                      <td className={styles.tableCell}>
                        <ToggleSwitch
                          value={row.status === 1}
                          onChange={(value) =>
                            handleToggleVisibility(value ? 1 : 0, row.id)
                          }
                        />
                      </td>
                    )}

                    <td>
                      <RowActionsDropdown
                        onViewProfile={() => {
                          handleEditSession(row.id);
                          dispatch(setReviewSessionTitle(row?.title));
                        }}
                        onRemoveUser={() => {
                          setDeleteTargetIdx(row.id);
                          setDeleteModalOpen(true);
                        }}
                        onReviewSession={() => {
                          handleReviewSession(row);
                          dispatch(setReviewSessionTitle(row?.title));
                        }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <div className="d-flex justify-content-between align-items-center pb-3 mt-3">
        <div className={styles.tableFooter}>
          Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
          {Math.min(currentPage * itemsPerPage, totalEntries)} of {totalEntries}{" "}
          entries
        </div>
        <div className={styles.paginationRow}>
          {/* <div className="d-flex align-items-center gap-2">
            <span style={{ fontSize: "14px", color: "#6B7280" }}>Show:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
              style={{
                padding: "4px 8px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
                background: "white",
              }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
            <span style={{ fontSize: "14px", color: "#6B7280" }}>entries</span>
          </div> */}
          <div className="d-flex align-items-center gap-2">
            <button
              className={styles.paginationBtn}
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <ChevronLeft size={18} />
            </button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                className={
                  currentPage === i + 1
                    ? `${styles.paginationBtn} ${styles.paginationBtnActive}`
                    : styles.paginationBtn
                }
                onClick={() => handlePageChange(i + 1)}
              >
                {i + 1 < 10 ? `0${i + 1}` : i + 1}
              </button>
            ))}
            <button
              className={styles.paginationBtn}
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>

      <UserProfileModal
        show={profileModalOpen}
        onHide={() => setProfileModalOpen(false)}
        user={selectedProfile}
      />
      <DeleteUserModal
        show={deleteModalOpen}
        onHide={() => setDeleteModalOpen(false)}
        onDelete={() => {
          // If multiple selected, delete all; else delete one
          if (
            selectedRows.length > 1 &&
            selectedRows.includes(deleteTargetIdx)
          ) {
            // Delete all selected
            // ... implement your delete logic here
            console.log("Delete multiple sessions:", selectedRows);
            handleToggleVisibility(9, selectedRows);
          } else {
            // Delete single
            // ... implement your delete logic here
            handleToggleVisibility(9, deleteTargetIdx);
          }
          setDeleteModalOpen(false);

          // Clear selections after delete
          setSelectedRows([]);

          // Refresh the current page to get updated data
          if (onPageChange) {
            const currentPageInfo = pagination?.currentPage || 1;
            const currentLimit = pagination?.itemsPerPage || 10;
            onPageChange(currentPageInfo, currentLimit);
          }
        }}
        message="Are you sure you want to delete this session? This action is permanent and cannot be undone."
        title="Remove Session"
        btnText="Remove Session"
        isMultiple={
          selectedRows.length > 1 && selectedRows.includes(deleteTargetIdx)
        }
        count={selectedRows.length}
      />
    </div>
  );
};

export default CustomTable;
